import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { isAxiosError } from "axios"
import { clickReferralApi } from "../../features/referrals/api/referrals.api"
import { registerEmailApi, verifyEmailApi, resendEmailCodeApi } from "../../features/auth/api/auth.api"
import { tokenStorage } from "../../shared/lib/token"
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  EnvelopeOpenIcon,
} from "@heroicons/react/16/solid"

const CLICK_TOKEN_KEY = "referral_click_token"

// TODO: заменить на реальные ссылки, когда приложение опубликуют
const APP_STORE_URL = "#"
const GOOGLE_PLAY_URL = "#"

const AppStoreBadge = () => (
  <a
    href={APP_STORE_URL}
    className="flex items-center gap-2.5 bg-gray-900 hover:bg-black text-white rounded-xl px-4 py-2.5 transition-colors"
  >
    <svg viewBox="0 0 384 512" className="w-5 h-5 fill-current flex-shrink-0">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
    </svg>
    <span className="text-left leading-tight">
      <span className="block text-[10px] text-gray-300">Скачать в</span>
      <span className="block text-sm font-semibold -mt-0.5">App Store</span>
    </span>
  </a>
)

const GooglePlayBadge = () => (
  <a
    href={GOOGLE_PLAY_URL}
    className="flex items-center gap-2.5 bg-gray-900 hover:bg-black text-white rounded-xl px-4 py-2.5 transition-colors"
  >
    <svg viewBox="0 0 512 512" className="w-5 h-5 flex-shrink-0">
      <path fill="#00d2ff" d="M99.6 471.2 320 261 99.5 40.8c-3.6 7-5.6 15.2-5.6 24.1v382.2c0 8.9 2 17.1 5.7 24.1z"/>
      <path fill="#00ff72" d="m320 261 55.6-55.6L145 68.4c-13.5-7.8-28.2-9.3-41-4.2L320 261z"/>
      <path fill="#ff3a44" d="M320 261 104 457.8c12.8 5.1 27.5 3.6 41-4.2l230.6-137.1L320 261z"/>
      <path fill="#ffcf00" d="M457.9 245.1 375.6 205.4 320 261l55.6 55.6 82.3-39.7c14.6-9.4 14.6-22.4 0-31.8z"/>
    </svg>
    <span className="text-left leading-tight">
      <span className="block text-[10px] text-gray-300">Доступно в</span>
      <span className="block text-sm font-semibold -mt-0.5">Google Play</span>
    </span>
  </a>
)

type Status = "checking" | "invalid" | "form" | "verify" | "success"

const Header = () => (
  <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
    <div className="max-w-3xl mx-auto px-6 h-16 flex items-center">
      <a href="/" className="flex items-center">
        <img src="/horizontal-1.png" alt="Vkonekte" className="h-32 w-auto" />
      </a>
    </div>
  </header>
)

const Shell = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50/70 via-white to-white text-gray-900">
    <Header />
    <main className="flex items-center justify-center px-4 py-16">
      {children}
    </main>
  </div>
)

export const ReferralLandingPage = () => {
  const { code } = useParams<{ code: string }>()

  const [status, setStatus] = useState<Status>("checking")

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirm, setPasswordConfirm] = useState("")
  const [phone, setPhone] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [verifyCode, setVerifyCode] = useState("")
  const [verifying, setVerifying] = useState(false)
  const [verifyError, setVerifyError] = useState<string | null>(null)
  const [resending, setResending] = useState(false)
  const [resendMessage, setResendMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!code) {
      setStatus("invalid")
      return
    }

    clickReferralApi(code)
      .then((res) => {
        localStorage.setItem(CLICK_TOKEN_KEY, res.data.click_token)
        setStatus("form")
      })
      .catch(() => {
        setStatus("invalid")
      })
  }, [code])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password !== passwordConfirm) {
      setError("Пароли не совпадают")
      return
    }

    setSubmitting(true)

    try {
      await registerEmailApi({
        email,
        password,
        phone_number: phone,
        referral_click_token: localStorage.getItem(CLICK_TOKEN_KEY) || "",
      })

      setStatus("verify")
    } catch {
      setError("Не удалось зарегистрироваться. Проверьте данные и попробуйте снова.")
    } finally {
      setSubmitting(false)
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setVerifyError(null)
    setVerifying(true)

    try {
      const res = await verifyEmailApi({ email, code: verifyCode })

      const { access_token, refresh_token, user_id } = res.data
      tokenStorage.set(access_token)
      localStorage.setItem("refresh_token", refresh_token)
      localStorage.setItem("user_id", user_id)
      localStorage.removeItem(CLICK_TOKEN_KEY)

      setStatus("success")
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 400) {
        setVerifyError("Неверный или истёкший код. Проверьте код или запросите новый.")
      } else {
        setVerifyError("Не удалось подтвердить код. Попробуйте снова.")
      }
    } finally {
      setVerifying(false)
    }
  }

  const handleResend = async () => {
    setResendMessage(null)
    setVerifyError(null)
    setResending(true)

    try {
      await resendEmailCodeApi(email)
      setResendMessage("Новый код отправлен на почту.")
    } catch {
      setVerifyError("Не удалось отправить код повторно. Попробуйте чуть позже.")
    } finally {
      setResending(false)
    }
  }

  if (status === "checking") {
    return (
      <Shell>
        <div className="w-10 h-10 border-4 border-gray-100 border-t-blue-500 rounded-full animate-spin" />
      </Shell>
    )
  }

  if (status === "invalid") {
    return (
      <Shell>
        <div className="max-w-md w-full text-center bg-white rounded-2xl border border-gray-100 shadow-xl p-8">
          <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
            <ExclamationTriangleIcon className="w-7 h-7 text-amber-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Ссылка недействительна</h1>
          <p className="text-sm text-gray-500 leading-relaxed">
            Эта пригласительная ссылка устарела или введена неверно. Попросите
            друга прислать актуальную ссылку — или перейдите на сайт напрямую.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-1.5 mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors shadow-sm shadow-blue-200"
          >
            На главную
            <ArrowRightIcon className="w-3.5 h-3.5" />
          </a>
        </div>
      </Shell>
    )
  }

  if (status === "verify") {
    return (
      <Shell>
        <form
          onSubmit={handleVerify}
          className="w-full max-w-md bg-white rounded-2xl border border-gray-100 shadow-xl p-8 space-y-5"
        >
          <div className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
              <EnvelopeOpenIcon className="w-7 h-7 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Подтвердите почту</h1>
            <p className="text-sm text-gray-500 mt-1">
              Мы отправили 6-значный код на <span className="font-medium text-gray-700">{email}</span>.
              Код действителен 15 минут.
            </p>
          </div>

          {verifyError && (
            <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">
              {verifyError}
            </div>
          )}

          {resendMessage && !verifyError && (
            <div className="rounded-lg bg-emerald-50 px-4 py-2 text-sm text-emerald-600">
              {resendMessage}
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Код из письма</label>
            <input
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-center text-xl tracking-[0.5em] font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition placeholder:text-gray-300 placeholder:tracking-[0.5em]"
            />
          </div>

          <button
            type="submit"
            disabled={verifying || verifyCode.length !== 6}
            className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors shadow-sm shadow-blue-200"
          >
            {verifying
              ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              : <CheckCircleIcon className="w-4 h-4" />}
            Подтвердить
          </button>

          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="w-full text-center text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50 font-medium transition-colors"
          >
            {resending ? "Отправляем..." : "Отправить код повторно"}
          </button>
        </form>
      </Shell>
    )
  }

  if (status === "success") {
    return (
      <Shell>
        <div className="max-w-md w-full text-center bg-white rounded-2xl border border-gray-100 shadow-xl p-8">
          <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <CheckCircleIcon className="w-7 h-7 text-emerald-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Всё отлично, регистрация пройдена!</h1>
          <p className="text-sm text-gray-500 leading-relaxed">
            Войдите в приложение Vkonekte под своим email и паролем, чтобы найти партнёров,
            пройти курсы GS Academy и начать пользоваться платформой.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
            <AppStoreBadge />
            <GooglePlayBadge />
          </div>
        </div>
      </Shell>
    )
  }

  return (
    <Shell>
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md bg-white rounded-2xl border border-gray-100 shadow-xl p-8 space-y-5"
      >
        <div>
          <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-600 text-xs font-semibold px-3 py-1.5 rounded-full border border-blue-100 mb-4">
            Приглашение от друга
          </span>
          <h1 className="text-2xl font-bold text-gray-900">Создайте аккаунт</h1>
          <p className="text-sm text-gray-500 mt-1">
            Присоединяйтесь к Vkonekte по приглашению
          </p>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition placeholder:text-gray-300"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Пароль</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition placeholder:text-gray-300"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Повторите пароль</label>
          <input
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            placeholder="••••••••"
            required
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition placeholder:text-gray-300"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Телефон</label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+7 000 000 00 00"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition placeholder:text-gray-300"
          />
        </div>

        <button
          type="submit"
          disabled={submitting || !email || !password || !passwordConfirm}
          className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors shadow-sm shadow-blue-200"
        >
          {submitting
            ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <CheckCircleIcon className="w-4 h-4" />}
          Зарегистрироваться
        </button>
      </form>
    </Shell>
  )
}